import { useState } from 'react';

import { useUpdateWorkspace } from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import {
  Button,
  Field,
  Input,
  PageHeader,
  Select,
  Textarea,
  Toggle,
} from '../../components/ui/index.js';

export function SettingsSecurity(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const update = useUpdateWorkspace(slug);

  const [tfa2faAdmin, setTfa2faAdmin] = useState(true);
  const [tfa2faAll, setTfa2faAll] = useState(false);
  const [tfaSms, setTfaSms] = useState(true);
  const [tfaBackup, setTfaBackup] = useState(true);
  const [ssoGoogle, setSsoGoogle] = useState(false);
  const [ssoMicrosoft, setSsoMicrosoft] = useState(false);
  const [ssoSaml, setSsoSaml] = useState(false);
  const [sessionLogout, setSessionLogout] = useState(true);
  const [sessionOneBrowser, setSessionOneBrowser] = useState(false);
  const [sessionNotify, setSessionNotify] = useState(true);
  const [pwdDigits, setPwdDigits] = useState(false);
  const [pwdSpecial, setPwdSpecial] = useState(false);
  const [ipWhitelistEnabled, setIpWhitelistEnabled] = useState(false);
  const [ipList, setIpList] = useState('');
  const [ipBlockOthers, setIpBlockOthers] = useState(false);
  const [ipNotifyOwner, setIpNotifyOwner] = useState(true);
  const [saved, setSaved] = useState(false);

  async function handleSave(): Promise<void> {
    await update.mutateAsync({
      security: {
        tfa_force_admin: tfa2faAdmin,
        tfa_force_all: tfa2faAll,
        tfa_sms_backup: tfaSms,
        tfa_backup_codes: tfaBackup,
        sso_google: ssoGoogle,
        sso_microsoft: ssoMicrosoft,
        sso_saml: ssoSaml,
        session_logout_on_pwd: sessionLogout,
        session_one_browser: sessionOneBrowser,
        session_notify_new_device: sessionNotify,
        pwd_require_digits: pwdDigits,
        pwd_require_special: pwdSpecial,
        ip_whitelist_enabled: ipWhitelistEnabled,
        ip_whitelist: ipList,
        ip_block_others: ipBlockOthers,
        ip_notify_owner: ipNotifyOwner,
      },
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="inbx-page">
      <PageHeader title="Безопасность" />
      <div className="inbx-page__body" style={{ maxWidth: 720 }}>
        <div className="inbx-card">
          <h4 className="inbx-card__title">Двухфакторная аутентификация</h4>
          <ToggleRow
            label="Принудительно для Admin и выше"
            checked={tfa2faAdmin}
            onChange={setTfa2faAdmin}
            note="нельзя выключить"
          />
          <ToggleRow
            label="Принудительно для всех операторов"
            checked={tfa2faAll}
            onChange={setTfa2faAll}
          />
          <ToggleRow label="Разрешить SMS как резерв" checked={tfaSms} onChange={setTfaSms} />
          <ToggleRow label="Резервные коды (10 штук)" checked={tfaBackup} onChange={setTfaBackup} />
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Single Sign-On</h4>
          <ToggleRow
            label="Google OAuth"
            checked={ssoGoogle}
            onChange={setSsoGoogle}
            note="Starter+"
          />
          <ToggleRow
            label="Microsoft / Azure AD"
            checked={ssoMicrosoft}
            onChange={setSsoMicrosoft}
            note="Business+"
          />
          <ToggleRow label="SAML 2.0" checked={ssoSaml} onChange={setSsoSaml} note="Enterprise" />
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Сессии</h4>
          <Field label="Время жизни access-токена">
            <Select defaultValue="15">
              <option value="15">15 минут</option>
              <option value="30">30 минут</option>
              <option value="60">1 час</option>
            </Select>
          </Field>
          <Field label="Время жизни refresh-токена">
            <Select defaultValue="30">
              <option value="7">7 дней</option>
              <option value="30">30 дней</option>
              <option value="90">90 дней</option>
            </Select>
          </Field>
          <ToggleRow
            label="Выход при смене пароля"
            checked={sessionLogout}
            onChange={setSessionLogout}
          />
          <ToggleRow
            label="Один браузер на устройство"
            checked={sessionOneBrowser}
            onChange={setSessionOneBrowser}
          />
          <ToggleRow
            label="Уведомление о новом устройстве"
            checked={sessionNotify}
            onChange={setSessionNotify}
          />
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Пароли</h4>
          <div className="inbx-form-row">
            <Field label="Мин. длина">
              <Input type="number" defaultValue={8} />
            </Field>
            <Field label="Блокировка после N попыток">
              <Input type="number" defaultValue={5} />
            </Field>
            <Field label="Cooldown, мин">
              <Input type="number" defaultValue={15} />
            </Field>
          </div>
          <ToggleRow label="Требовать цифры" checked={pwdDigits} onChange={setPwdDigits} />
          <ToggleRow label="Требовать спецсимволы" checked={pwdSpecial} onChange={setPwdSpecial} />
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">IP-whitelist</h4>
          <div
            style={{
              color: 'var(--inbx-text-tertiary)',
              fontSize: 'var(--inbx-fz-sm)',
              marginBottom: 12,
            }}
          >
            Доступно на Business и Enterprise планах. Один IP/CIDR на строку.
          </div>
          <ToggleRow
            label="Включить IP-whitelist"
            checked={ipWhitelistEnabled}
            onChange={setIpWhitelistEnabled}
          />
          <Field label="Разрешённые IP/подсети">
            <Textarea
              rows={4}
              placeholder={'195.85.24.0/24\n78.140.128.15'}
              value={ipList}
              onChange={(e) => setIpList(e.target.value)}
            />
          </Field>
          <ToggleRow
            label="Блокировать все остальные"
            checked={ipBlockOthers}
            onChange={setIpBlockOthers}
          />
          <ToggleRow
            label="Уведомить Owner при блокировке"
            checked={ipNotifyOwner}
            onChange={setIpNotifyOwner}
          />
          <div style={{ marginTop: 12 }}>
            <Button variant="primary" onClick={handleSave} disabled={update.isPending}>
              {saved ? '✓ Сохранено' : update.isPending ? 'Сохранение…' : 'Сохранить'}
            </Button>
          </div>
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Rate Limiting</h4>
          <div className="inbx-form-row">
            <Field label="Анонимные (req/мин)">
              <Input type="number" defaultValue={60} />
            </Field>
            <Field label="Авторизованные (req/мин)">
              <Input type="number" defaultValue={600} />
            </Field>
            <Field label="Виджет (req/мин/IP)">
              <Input type="number" defaultValue={10} />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
  note,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  note?: string;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
      <Toggle checked={checked} onChange={() => onChange(!checked)} label={label} />
      {note && (
        <span style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-xs)' }}>
          · {note}
        </span>
      )}
    </div>
  );
}
